<script lang="ts">import { onMount } from 'svelte';
import { fly } from 'svelte/transition';
import { getEnvironmentFromHostname } from '../../js/urlUtils';
import ChatInput from './ChatInput.svelte';
import MessageBubble from './MessageBubble.svelte';
import TypingIndicator from './TypingIndicator.svelte';
import StartupAnimation from './StartupAnimation.svelte';
import { useChatAPI } from './hooks/useChatAPI';
import { useLocalStorage } from './hooks/useLocalStorage';
import { getTracking, getOneTrustContext } from '../../js-v2/tracking';
import { getUrlPath } from '../../js-v2/urlUtils';
const { onClose = undefined, isFirstTimeOpen = false, } = $props();
const trackFn = getTracking();
const oneTrustStore = getOneTrustContext();
const chatEndpoint = '/api/v1/search/chat';
const functionalityTrackingEnabled = $derived($oneTrustStore.functionalityTrackingEnabled);
let messages = $state([]);
let conversationId = $state(undefined);
let isLoading = $state(false);
let error = $state(null);
let messagesContainer = $state();
let showStartupAnimation = $state(false);
let showScrollToBottom = $state(false);
let scrollListenerAttached = $state(false);
let scrollTimeout = $state(null);
let isProd = $state(false);
let currentAbortController = $state(null);
const promptOverrides = $state(undefined);
let inputAreaElement = $state();
let headerElement = $state();
let apiOrigin = $state('');
const { sendMessage } = useChatAPI(() => apiOrigin + chatEndpoint);
const { saveToStorage, loadFromStorage } = useLocalStorage('ask-square');
function handleShouldShowScrollToBottom() {
    if (scrollTimeout) {
        clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(() => {
        if (messagesContainer) {
            const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            showScrollToBottom = distanceFromBottom > 50 && messages.length > 0;
        }
        scrollTimeout = null;
    }, 0);
}
// Update CSS custom properties based on visual viewport and actual element heights
function updateContainerHeights() {
    if (messagesContainer && headerElement && inputAreaElement && window.visualViewport) {
        const headerHeight = headerElement.offsetHeight;
        const inputHeight = inputAreaElement.offsetHeight;
        const viewportOffsetTop = window.visualViewport.offsetTop;
        const viewportHeight = window.visualViewport.height;
        const documentHeight = document.documentElement.clientHeight;
        // Position header at the top of the visible viewport
        if (headerElement) {
            headerElement.style.top = `${viewportOffsetTop}px`;
        }
        // Calculate available space for messages
        const availableTop = headerHeight + viewportOffsetTop;
        messagesContainer.style.setProperty('--header-height', `${availableTop}px`);
        if (inputAreaElement) {
            // Calculate where the bottom of the visual viewport is relative to the document
            const visualViewportBottom = viewportOffsetTop + viewportHeight;
            const bottomOffset = documentHeight - visualViewportBottom;
            inputAreaElement.style.bottom = `${bottomOffset}px`;
            // Update messages container bottom to account for input area position
            const messagesBottom = bottomOffset + inputHeight;
            messagesContainer.style.setProperty('--input-height', `${messagesBottom}px`);
        }
    }
}
onMount(() => {
    const env = getEnvironmentFromHostname(window.location.hostname);
    isProd = env === 'production';
    if (isProd) {
        apiOrigin = 'https://api.squareup.com';
    }
    const saved = functionalityTrackingEnabled ? loadFromStorage() : undefined;
    if (saved && Array.isArray(saved.messages) && saved.messages.length > 0) {
        messages = saved.messages;
        conversationId = saved.conversationId ?? undefined;
    }
    else if (isFirstTimeOpen) {
        showStartupAnimation = true;
    }
    // generate a new conversation id if one is not provided
    if (!conversationId) {
        conversationId = crypto.randomUUID();
    }
    // Update heights after initial render
    setTimeout(() => {
        updateContainerHeights();
    }, 100);
    // Handle visual viewport changes, mainly for when mobile keyboard opens or closes
    if (window.visualViewport) {
        let debounceTimeout = null;
        const handleViewportScroll = () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
            debounceTimeout = setTimeout(() => {
                requestAnimationFrame(() => {
                    updateContainerHeights();
                    handleShouldShowScrollToBottom();
                });
            }, 100);
        };
        // only detect scroll events from the visual viewport to not interfere with iOS Safari's native resize handling
        window.visualViewport.addEventListener('scroll', handleViewportScroll);
        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
            window.visualViewport.removeEventListener('scroll', handleViewportScroll);
        };
    }
});
$effect(() => {
    if (messages.length > 0) {
        if (functionalityTrackingEnabled) {
            saveToStorage({ messages, conversationId: conversationId ?? null });
        }
        setTimeout(() => {
            scrollToBottom();
        }, 0);
    }
});
$effect(() => {
    if (messagesContainer && !scrollListenerAttached) {
        messagesContainer.addEventListener('scroll', handleShouldShowScrollToBottom);
        scrollListenerAttached = true;
    }
});
// Update container heights when loading state or messages change
$effect(() => {
    if (isLoading || messages.length > 0) {
        setTimeout(() => {
            updateContainerHeights();
            scrollToBottom();
        }, 50);
    }
});
async function handleSendMessage(content) {
    if (!content.trim() || isLoading)
        return;
    const userMessage = {
        id: crypto.randomUUID(),
        conversationId,
        content: content.trim(),
        sender: 'user',
        timestamp: new Date(),
    };
    messages = [...messages, userMessage];
    showStartupAnimation = false;
    isLoading = true;
    error = null;
    currentAbortController = new AbortController();
    try {
        // Filter out cancelled messages from chat history
        const chatHistory = messages.filter((msg) => !msg.isCancelled);
        // TODO: handle abort?
        trackFn({
            eventName: 'Chat Message Sent',
            properties: {
                currentPage: getUrlPath(),
                conversationId,
                messageText: content.trim(),
                messageLength: content.length,
                timestamp: new Date().toISOString(),
            },
        });
        const startTime = Date.now();
        const response = await sendMessage(content.trim(), conversationId, isProd ? undefined : promptOverrides, chatHistory, currentAbortController.signal);
        const assistantMessage = {
            id: crypto.randomUUID(),
            conversationId,
            content: response.response,
            sender: 'assistant',
            timestamp: new Date(),
            products: response.products,
            links: response.links,
            suggestions: response.suggestions,
            debugInfo: isProd ? undefined : response.metadata,
        };
        messages = [...messages, assistantMessage];
        trackFn({
            eventName: 'Bot Response Shown',
            properties: {
                currentPage: getUrlPath(),
                conversationId,
                responseText: assistantMessage.content.trim(),
                responseLength: assistantMessage.content.length,
                responseTime: Date.now() - startTime,
            },
        });
    }
    catch (err) {
        // Handle cancelled requests
        if (err.message === 'Request was cancelled') {
            messages = messages.map((msg) => {
                if (msg.id === userMessage.id) {
                    return { ...msg, isCancelled: true };
                }
                return msg;
            });
        }
        else {
            error = err.message;
            const errorMessage = {
                id: crypto.randomUUID(),
                conversationId,
                content: error ?? 'Something went wrong. Please try again.',
                sender: 'assistant',
                timestamp: new Date(),
                isError: true,
            };
            messages = [...messages, errorMessage];
            if (!isProd) {
                console.error(`SendMessage error: ${err.message}`);
            }
        }
        trackFn({
            eventName: 'Bot Response Error',
            properties: {
                currentPage: getUrlPath(),
                conversationId,
                errorMessage: err.message,
            },
        });
    }
    finally {
        isLoading = false;
        currentAbortController = null;
    }
}
function handleFeedback(messageId, type, reason) {
    let messageContent = '';
    messages = messages.map((msg) => {
        if (msg.id === messageId) {
            messageContent = msg.content;
            return {
                ...msg,
                feedback: {
                    type,
                    submitted: type === 'positive' || (type === 'negative' && reason !== undefined),
                    reason,
                },
            };
        }
        return msg;
    });
    // Save updated messages to storage
    if (functionalityTrackingEnabled) {
        saveToStorage({ messages, conversationId: conversationId });
    }
    trackFn({
        eventName: 'Response Rated',
        properties: {
            currentPage: getUrlPath(),
            messageText: messageContent,
            responseId: messageId,
            ratingValue: type,
            feedbackReason: reason,
            conversationId,
        },
    });
}
function scrollToBottom() {
    if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight - 50;
    }
}
function clearConversation() {
    if (messages.length > 0) {
        showStartupAnimation = true;
        messages = [];
        // generate a new conversation id
        conversationId = crypto.randomUUID();
        error = null;
        if (functionalityTrackingEnabled) {
            saveToStorage({ messages: [], conversationId });
        }
        trackFn({
            eventName: 'New Chat Started',
            properties: {
                currentPage: getUrlPath(),
                timestamp: new Date().toISOString(),
                conversationId,
            },
        });
    }
}
function handleClose() {
    if (onClose) {
        onClose();
    }
}
function handleNewChat() {
    clearConversation();
}
function handleScrollToBottom() {
    scrollToBottom();
    showScrollToBottom = false;
}
function handleStop() {
    if (currentAbortController) {
        currentAbortController.abort();
    }
}
</script>

<div class="chat-container">
  <div class="chat-header" bind:this={headerElement}>
    <div class="header-content">
      <button class="close-button" aria-label="Close chat" title="Close" onclick={handleClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="header-title">Ask Square</h2>
      <button class="new-chat-button" aria-label="Menu" title="Menu" onclick={handleNewChat}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="false"
          class="icon-pencil-in-box-7wvd24h0g"
          role="img"
          style="display: inline; width: 24px; height: 24px"
        >
          <path
            d="M11.5791 5.50014H5.5V18.5001H18.5V13.6076H20.5V19.5001C20.5 20.0524 20.0523 20.5001 19.5 20.5001H4.5C3.94772 20.5001 3.5 20.0524 3.5 19.5001V4.50014C3.50003 3.94788 3.94773 3.50014 4.5 3.50014H11.5791V5.50014Z"
            fill="currentColor"
          ></path>
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M18.5693 2.05385C18.9615 1.73566 19.5387 1.75833 19.9043 2.12221L22.5215 4.72768C22.7094 4.91482 22.8159 5.16951 22.8164 5.43471C22.8167 5.69966 22.7114 5.95402 22.5244 6.14174L16.7012 11.9845C16.359 12.3277 15.9389 12.5833 15.4766 12.7287L12.1162 13.7853C11.7617 13.8967 11.3738 13.8022 11.1104 13.5402C10.8472 13.2782 10.751 12.8912 10.8604 12.5363L11.8975 9.1808C12.0423 8.71236 12.3 8.28647 12.6475 7.94057L18.4932 2.12221L18.5693 2.05385ZM14.0586 9.35854C13.9428 9.47381 13.8568 9.61554 13.8086 9.77162L13.334 11.3048L14.877 10.8204C15.0309 10.772 15.1712 10.6867 15.2852 10.5724L20.4014 5.43861L19.1982 4.24232L14.0586 9.35854Z"
            fill="currentColor"
          ></path>
        </svg>
      </button>
    </div>
  </div>

  <div class="messages-container" bind:this={messagesContainer}>
    {#if showStartupAnimation}
      <StartupAnimation />
    {:else}
      <div class="messages-list">
        {#each messages as message}
          <div class="message-wrapper" transition:fly={{ y: 20, duration: 300 }}>
            <MessageBubble {message} onFeedback={handleFeedback} {trackFn} />
          </div>
        {/each}

        {#if isLoading}
          <div class="loading-wrapper" transition:fly={{ y: 20, duration: 300, delay: 100 }}>
            <TypingIndicator />
          </div>
        {/if}
      </div>
    {/if}

    <button
      class="scroll-to-bottom-button"
      class:hidden={!showScrollToBottom}
      onclick={handleScrollToBottom}
      aria-label="Scroll to bottom"
      transition:fly={{ y: 20, duration: 200 }}
    >
      <svg xmlns:xlink="http://www.w3.org/1999/xlink" data-name="arrow-down" viewBox="0 0 24 24" width="24" height="24">
        <path
          d="M13 17.5864L18.793 11.7935L20.207 13.2075L12.707 20.7075C12.3166 21.0977 11.6834 21.0977 11.293 20.7075L3.79297 13.2075L5.20703 11.7935L11 17.5864V4.00049H13V17.5864Z"
          fill="currentColor"
        ></path>
      </svg>
    </button>
  </div>

  <div class="input-area" bind:this={inputAreaElement}>
    <ChatInput
      onSendMessage={handleSendMessage}
      onStop={handleStop}
      disabled={isLoading}
      placeholder="Ask about Square"
    />
    <div class="disclaimer">AI can make mistakes. Please check responses.</div>
  </div>
</div>

<style>@charset "UTF-8";
@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.chat-container {
  display: flex;
  flex-direction: column;
  width: 664px;
  height: calc(100vh - 32px);
  background: #ffffff;
  overflow: hidden;
  position: relative;
  font-family: "Square Sans Text VF", "Square Sans Text", Helvetica, Arial, "Hiragino Sans", "ヒラギノ角ゴ Pro W3", メイリオ, Meiryo, "ＭＳ Ｐゴシック", sans-serif;
}
@media (max-width: 739px) {
  .chat-container {
    width: 100dvw;
    height: auto;
    max-height: 100dvh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}

.chat-header {
  background-color: #ffffff;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
@media (max-width: 739px) {
  .chat-header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 20;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }
}
.chat-header .header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.chat-header .close-button,
.chat-header .new-chat-button {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  width: 40px;
  height: 40px;
  background: #f0f0f0;
  border: none;
  padding-block: 1px;
  padding-inline: 8px;
  cursor: pointer;
  color: #666666;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-header .close-button:focus-visible:not(:active),
.chat-header .new-chat-button:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.chat-header .close-button:focus,
.chat-header .new-chat-button:focus {
  background: transparent;
  outline: none;
}
.chat-header .close-button:hover,
.chat-header .new-chat-button:hover {
  background: transparent;
}
.chat-header .close-button:hover,
.chat-header .new-chat-button:hover {
  background: #e0e0e0;
  color: #333333;
}
.chat-header .close-button:focus,
.chat-header .new-chat-button:focus {
  outline: 2px solid #006aff;
  outline-offset: 2px;
}
.chat-header .header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  line-height: 1.2;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 20px;
  background: #ffffff;
  position: relative;
  min-height: 0;
  --header-height: 73px;
  --input-height: 155px;
  transition: top 0.5s ease-in-out, bottom 0.5s ease-in-out;
}
@media (max-width: 739px) {
  .messages-container {
    -webkit-overflow-scrolling: touch;
    position: absolute;
    top: var(--header-height);
    bottom: var(--input-height);
    left: 0;
    right: 0;
    overflow-y: auto;
    padding: 8px;
  }
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading-wrapper {
  padding: 8px 0;
}

.scroll-to-bottom-button {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  position: sticky;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  background-color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  z-index: 10;
}
.scroll-to-bottom-button:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.scroll-to-bottom-button:focus {
  background: transparent;
  outline: none;
}
.scroll-to-bottom-button:hover {
  background: transparent;
}
.scroll-to-bottom-button:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}
.scroll-to-bottom-button:active {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.scroll-to-bottom-button:focus {
  outline: 2px solid #006aff;
  outline-offset: 2px;
}
.scroll-to-bottom-button svg {
  transition: transform 0.2s ease;
}
.scroll-to-bottom-button:hover svg {
  transform: translateY(1px);
}

.input-area {
  padding: 16px 20px 8px 20px;
  background: white;
  flex-shrink: 0;
}
@media (max-width: 739px) {
  .input-area {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 20;
    padding-top: 4px;
    padding-bottom: 8px;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
  }
}
.input-area .disclaimer {
  font-size: 12px;
  color: #999999;
  text-align: center;
  margin-top: 8px;
  margin-bottom: 4px;
  line-height: 1.4;
  padding: 0 4px;
}

.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}</style>
